import React, { useEffect, useState } from 'react';
import { Select, Button, Row, Col, Form, Divider, TreeSelect, Popconfirm, notification } from 'antd';
import BreadcrumbComponent from '../../../../components/Breadcrumb';
import { IBreadcrumbItem } from '../../../../models/breadcrumb';
import { useTranslation } from 'react-i18next';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { convertToTreeData } from '../../../../utils/commonFunctions';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState, useAppDispatch } from '../../../../app/store';
import { useSelector } from 'react-redux';
import { fetchReportsMinistriesMappingList, mapReportsMinistry } from '../reportsMappingApi';
import { fetchMinistries, fetchReports } from '../../../master/masterApi';

const { Option } = Select;

interface AddUpdateReportsMappingProps {
    isEdit: boolean;

}

const AddUpdateReportsMapping: React.FC<AddUpdateReportsMappingProps> = (props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [form] = Form.useForm();
    const { reportsMinistries } = useSelector((state: RootState) => state.reportsMinistryMapping);
    const { ministryId } = useParams<{ ministryId: string }>();

    const [isFormDirty, setIsFormDirty] = useState(false);
    const { reports, ministries } = useSelector((state: RootState) => state.masterData);
    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

    const treeData = convertToTreeData(ministries);
    useEffect(() => {
        dispatch(fetchReportsMinistriesMappingList());

    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchMinistries());
    }, [dispatch]);

    useEffect(() => {
        if (!(reports?.length)) {
            dispatch(fetchReports());
        }
        if (ministryId) {
            updateReports(parseInt(ministryId));
        }
    }, [reportsMinistries]);

    useEffect(() => {
        if (ministryId) {
            updateReports(parseInt(ministryId));
        }
    }, []);

    const breadcrumbItems: Array<IBreadcrumbItem> = [
        { title: t('home'), path: '/home' },
        {
            title: t('menu.setup.reports-mapping'),
            onClick: () => navigate('/setup/reports-mapping') // Call the onCancel function when clicked
        },
        {
            title: props.isEdit ? t('mapping.edit.button') : t('mapping.add.button')
        }
    ];


    // Function to get reports by ministry id
    const getReportsByMinistryId = (ministryId: number): number[] => {
        const ministry = reportsMinistries.find((ministry) => ministry.id === ministryId);
        if (!ministry) {
            return [];
        }
        return ministry.reports.map((report: any) => report.id);
    };

    //Update indicators binding on the change of ministry
    const updateReports = (ministryId: number) => {
        if (ministryId) {
            const reports = getReportsByMinistryId(ministryId);
            form.setFieldsValue({
                ministry: ministryId,
                reports: reports
            });
        }
    }

    const onFormValuesChange = (changedValues: any) => {
        setIsFormDirty(form.isFieldsTouched());
    };



    const onFinish = (values: any) => {
        const reportsMinistryMapping = {
            nodeId: values?.ministry,
            reportsId: values?.reports
        };

        dispatch(mapReportsMinistry(reportsMinistryMapping))
            .unwrap()
            .then((response) => {
                form.resetFields();
                if (response?.status === 'success') {
                    notification.success({ message: t('success'), description: response?.message });
                    navigate('/setup/reports-mapping')
                }
            });
    };

    const expandAllKeys = (nodes: any) => {
        const keys: string[] = [];
        const traverse = (nodeList: any) => {
            nodeList.forEach((node: any) => {
                keys.push(node.key);
                if (node.children) {
                    traverse(node.children);
                }
            });
        };
        traverse(nodes);
        return keys;
    };


    return (
        <Row className='mainTextArea' style={{ width: '100%' }}>
            <Col xs={24} sm={24} md={24} lg={24} xl={24} >
                <Row gutter={[16, 16]} style={{ borderBottom: '1px solid #CCC', marginBottom: '10px', paddingBottom: '10px' }}>
                    <Col xs={24} sm={24} md={24} lg={24} xl={24} >
                        <h4>{t('menu.setup.reports-mapping')}</h4>
                        <BreadcrumbComponent items={breadcrumbItems} separator='>' />
                    </Col>
                </Row>
            </Col>

            <Form
                style={{ width: '100%' }}
                form={form}
                layout="vertical"
                onValuesChange={onFormValuesChange}
                onFinish={onFinish}>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label={t('mapping-indicator.ministry-department')}
                            name="ministry"
                            rules={[{ required: true, message: t('mapping-indicator.ministry-department.req.error') }]}>

                            <TreeSelect
                                disabled={ministryId ? true : false}
                                treeData={treeData}
                                onChange={(value) => updateReports(parseInt(value))}
                                placeholder={t('mapping-indicator.label-ministry.department')}
                                allowClear
                                showSearch
                                filterTreeNode={(input, treeNode) => {
                                    const title = treeNode?.title;
                                    if (typeof title === 'string') {
                                        return title.toLowerCase().includes(input.toLowerCase());
                                    }
                                    return false;
                                }}
                                onSearch={(value) => {
                                    if (value) {
                                        setExpandedKeys(expandAllKeys(treeData)); // Expand all nodes during search
                                    } else {
                                        setExpandedKeys([]); // Collapse all nodes when search is cleared
                                    }
                                }}
                                onDropdownVisibleChange={(open) => {
                                    if (!open) {
                                        setExpandedKeys([]); // Collapse tree when dropdown is closed
                                    }
                                }}
                                treeExpandedKeys={expandedKeys}
                                onTreeExpand={(keys) => setExpandedKeys(keys.map(String))} // Cast keys to strings

                            />
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Form.Item
                            label={t('reports-mapping.reports')}
                            name="reports"
                            className='dropdown'
                            rules={[{ required: true, message: t('reports-mapping.reports.error.req') }]}>
                            <Select
                                placeholder={t('reports-mapping.reports.placeholder')}
                                mode="multiple"
                                className='indicator_list'
                                showSearch={true}
                                filterOption={(input, option) => {
                                    const children = option?.children as string | undefined;
                                    return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
                                }}
                                allowClear
                            >
                                {reports.map(report => (
                                    <Option key={report.id} value={report.id}>
                                        {report.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Divider style={{ margin: '15px 0 25px 0', borderBottom: '1px solid #CCC' }}></Divider>
                    <Col span={24} className='actionButHolder'>
                        <Form.Item>
                            <Button type="primary" htmlType="submit" icon={<i className="fa fa-save" />} disabled={!isFormDirty}>
                                <PlusOutlined /> {t('btn.save')}
                            </Button>
                            {isFormDirty ?
                                <Popconfirm
                                    title={t('confirmation')}
                                    description={t('cancel.confirmation.msg')}
                                    okText={t('yes')}
                                    cancelText={t('no')}
                                    onConfirm={() => navigate('/setup/reports-mapping')}>
                                    <Button type="primary" ><CloseOutlined /> {t('btn.cancel')}</Button>
                                </Popconfirm>
                                :
                                <Button type="primary" onClick={() => navigate('/setup/reports-mapping')} icon={<i className="fa fa-times" />}>
                                    <CloseOutlined /> {t('btn.cancel')}
                                </Button>
                            }
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Row >
    );
};

export default AddUpdateReportsMapping;